import React from "react";
import RowItem from "./RowItem";
import Sheet from "@mui/joy/Sheet";
import Button from "@mui/joy/Button";
import CssBaseline from "@mui/joy/CssBaseline";
import useScreenWidth from "../store/useScreenWidth";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { GridColDef } from "@mui/x-data-grid";
import { FieldErrors } from "../domain/helpers/Errors";
import { useLanguageStore } from "../store/useLanguageStore";
import { useDarkModeStore } from "../store/useDarkModeStore";
import { darkModeBackgroudInputs } from "../utils/constants";
import { Modal, ModalContent, StyledBackdrop, } from "./TopBar/Buttons/SearchTopBarModal";

interface ModalFormProps<T extends { [key: string]: any }> {
  form: T;
  open: {
    add: boolean;
    edit: boolean;
    view: boolean;
  };
  errors: FieldErrors;
  columns: GridColDef[];
  setOpen: React.Dispatch<
    React.SetStateAction<{
      add: boolean;
      edit: boolean;
      view: boolean;
    }>
  >;
  handleSave: (id?: string) => Promise<void>;
  setForm: React.Dispatch<React.SetStateAction<T>>;
}

export const ModalForm = <T extends { [key: string]: any }>({
  open,
  form,
  errors,
  columns,
  setOpen,
  setForm,
  handleSave,
}: ModalFormProps<T>) => {

  const { add, edit, view } = open;
  const { language } = useLanguageStore();
  const { darkMode } = useDarkModeStore();
  const isSmallScreen = useScreenWidth();

  const handleClose = () => setOpen({ ...open, add: false, edit: false, view: false });

  const handleSubmit = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    handleSave();
  };

  const modalSize = () => {
    if (isSmallScreen) return 300;
    else return columns && columns.length > 4 ? 800 : 600;
  };

  return (
    <Modal
      open={add || edit || view}
      onClose={handleClose}
      slots={{ backdrop: StyledBackdrop }}
    >
      <ModalContent sx={{ width: "fit-content" }}>
        <CssBaseline />
        <Sheet
          sx={{
            my: 0,
            py: 0,
            px: 0,
            gap: 0,
            display: "flex",
            width: modalSize,
            borderRadius: "md",
            flexDirection: "column",
            boxShadow: "8px 8px 30px rgba(0, 0, 0, 0.2)",
            background: darkMode ? darkModeBackgroudInputs : "white",
          }}
        >
          <div className="d-flex flex-row p-2 pt-3 pb-0">
            <div className="input-group flex-nowrap">
              <span
                className={`input-group-text border-0 fw-bold`}
                id="addon-wrapping"
                style={{
                  color: darkMode ? "white" : "#32475C6F",
                  cursor: "default",
                  background: darkMode ? darkModeBackgroudInputs : "white",
                }}
              >
                {language[add ? "Add" : edit ? "Edit" : 'View']}
              </span>
              <span
                className="input-group-text border-0 ms-auto"
                role="button"
                style={{
                  color: darkMode ? "white" : "#32475C61",
                  background: darkMode ? darkModeBackgroudInputs : "white",
                }}
                onClick={handleClose}
              >
                [esc]{" "}
                <CloseOutlinedIcon
                  sx={{
                    width: "16px",
                    height: "16px",
                    marginLeft: "13px",
                    color: darkMode ? "white" : "#909BAF",
                  }}
                />
              </span>
            </div>
          </div>
          <hr style={{ color: "grey" }} />
          <form
            className={`ps-4 pe-4 pt-2 pb-2 ${columns && columns.length > 6
              ? "module-form-grid"
              : "d-flex flex-column"
              } ${isSmallScreen && "shortcuts-grid-column-1fr"}`}
            style={{ maxHeight: 500, overflowY: "auto" }}
          >
            {columns?.map((col, index) => (
              <RowItem
                key={col.field || index.toString()}
                form={form}
                view={view}
                column={col}
                errors={errors}
                setForm={setForm}
              />
            ))}
          </form>
          <hr style={{ color: "grey" }} />
          <div
            className="d-flex align-items-center mt-2 mb-4 gap-2 ps-4 pe-4 w-50 ms-auto"
            style={{ fontSize: "16px" }}
          >
            <Button
              className={`w-50 ${view && "ms-auto"}`}
              sx={{
                background: darkMode ? darkModeBackgroudInputs : "white",
                border: `1px solid var(--btn-color)`,
                color: darkMode ? 'white' : "var(--btn-color)",
                "&:hover": {
                  background: darkMode ? darkModeBackgroudInputs : "white",
                  opacity: .7,
                },
              }}

              onClick={handleClose}
            >
              {language["Cancel"]}
            </Button>
            {!view && (
              <Button
                className="w-50"
                sx={{
                  background: "var(--btn-color)",
                  color: "white",
                  "&:hover": {
                    background: "var(--btn-color)",
                    opacity: .7
                  },
                }}
                onClick={(e) => handleSubmit(e)}
              >
                {language["Save"]}
              </Button>
            )}
          </div>
        </Sheet>
      </ModalContent>
    </Modal>
  );
};
export default ModalForm;
